import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../../common/library/Button/Button';
import { TestIds } from '../../../assets/testIds';

type ContentContactSalesDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting?: boolean;
};

/**
 * Shown when locked content requires Advanced or Alliance, which cannot be bought
 * in app - the Content chart on product's "SN Logic on the Edge platform" page
 * gives those pieces a "Contact Sales" call to action rather than "Upgrade to
 * unlock" (SN-996).
 *
 * Separate from the partner `ContactSalesDialog` because there is no partner to
 * name here: confirming posts to the company-scoped
 * `POST /companies/:id/contact-sales`, which sends sales the same profile details
 * without any partner lines in the email.
 */
export function ContentContactSalesDialog({
  open,
  onClose,
  onConfirm,
  submitting,
}: ContentContactSalesDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="content-contact-sales-dialog-title"
      aria-describedby="content-contact-sales-dialog"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: theme.spacing(84),
          borderRadius: theme.spacing(1.5),
        },
      }}
    >
      <DialogContent sx={{ padding: theme.spacing(3) }}>
        <IconButton
          aria-label={t('button.close') || 'Close'}
          onClick={onClose}
          data-testid={TestIds.contentFeed.contactSalesDialogCloseBtn}
          sx={{
            position: 'absolute',
            right: theme.spacing(2),
            top: theme.spacing(2),
            color: theme.palette.grey[700],
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Typography
            variant="h4"
            id="content-contact-sales-dialog-title"
            sx={{ paddingRight: theme.spacing(4) }}
          >
            {t('content.contactSales.title')}
          </Typography>
          <Typography
            variant="body8"
            color={theme.palette.grey[700]}
            id="content-contact-sales-dialog"
            data-testid={TestIds.contentFeed.contactSalesDialogDescription}
          >
            {t('content.contactSales.description')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <OptimusButton
              customVariant="primary"
              size="m"
              fullWidth={false}
              onClick={onConfirm}
              disabled={!!submitting}
              buttonText={t('button.confirm') || 'Confirm'}
              data-testid={TestIds.contentFeed.contactSalesConfirmBtn}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
